import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/dist/query/react';
import {
  HwIdResponse,
  LicenseComparision,
  LicenseStatus,
  LicenseStatusResponse,
  LicenseUploadResponse,
} from '../models';
import { SettingsState, getSettings } from './settings.helpers';

const rawBaseQuery = fetchBaseQuery();
const dynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const settings = getSettings(api.getState() as SettingsState);
  const urlEnd = typeof args === 'string' ? args : args.url;
  const adjustedUrl = `${settings?.servers.serverLicense ?? ''}/api/v1/${urlEnd}`;
  const adjustedArgs = typeof args === 'string' ? adjustedUrl : { ...args, url: adjustedUrl };
  return rawBaseQuery(adjustedArgs, api, extraOptions);
};

const licenseStatusTag = 'LicenseStatus' as const;

export const licenseApi = createApi({
  reducerPath: 'licenseApi',
  tagTypes: [licenseStatusTag],
  baseQuery: dynamicBaseQuery,
  endpoints: builder => ({
    getHwId: builder.query<string, void>({
      query: () => 'hwid',
      transformResponse: (response: HwIdResponse) => response.data.hwid,
    }),
    getLicensesStatus: builder.query<LicenseStatus[], void>({
      query: () => 'licenses/status',
      transformResponse: (response: LicenseStatusResponse) => response.data ?? [],
      transformErrorResponse: () => [],
      providesTags: [licenseStatusTag],
    }),
    uploadLicense: builder.mutation<LicenseComparision, FormData>({
      query: body => ({
        url: 'licenses/file',
        method: 'POST',
        body,
      }),
      transformResponse: (response: LicenseUploadResponse) => response.data,
    }),
    activateLicense: builder.mutation<void, string>({
      query: id => ({
        url: `licenses/file/${id}`,
        method: 'PATCH',
      }),
      invalidatesTags: [licenseStatusTag],
    }),
  }),
});

export const { useGetHwIdQuery, useGetLicensesStatusQuery, useUploadLicenseMutation, useActivateLicenseMutation } =
  licenseApi;
